import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DatePicker } from '~/components/Form/next/FormDatePicker';

import { Button, Divider } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import Loader from 'components/Loader';
import { SpaceBlocking, SpaceBooking } from 'components/SpaceBooking/types';
import { convertTimeToString } from 'helpers/TimeHelper';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useApi } from '../../../../hooks/useApi';

interface Props {
    clubSlug?: string;
    element: Partial<
        Pick<
            SpaceBooking | SpaceBlocking,
            'id' | 'start' | 'exemptions' | 'repeat_period' | 'repeat_until' | 'spaces' | 'start_time' | 'end_time'
        >
    >;
}

export type OverlappingAssistantElement = Props['element'];

interface OverlapElement {
    id: number;
    title: string;
    description: string | null;
    start: string;
    start_time: number;
    end_time: number;
}

type OverlapsResponse = {
    blockings: OverlapElement[];
    bookings: OverlapElement[];
    exemptions: { start: string; end: string }[];
};

export const OverlappingAssistant: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    element,
    clubSlug: _clubSlug
}) => {
    const clubSlug = useClubSlug(_clubSlug);
    const { getValues, setValue, watch } = useFormContext() || {};
    const [isLoading, setIsLoading] = useState(true);
    const [newExemption, setNewExemption] = useState<{ start: Date | string; end: Date | string }>({
        start: '',
        end: ''
    });
    const [initialExemptions, setInitialExemptions] = useState([]);
    const appliedExemptions = watch?.('exemptions') ?? [];
    const [data, setData] = useState<OverlapsResponse>({ blockings: [], bookings: [], exemptions: [] });
    const allOverlaps = [...(data.blockings || []), ...(data.bookings || [])];
    const api = useApi();

    const checkOverlaps = useCallback(
        (element: OverlappingAssistantElement) => {
            setIsLoading(true);
            api({
                url: `space/overlaps/${clubSlug}`,
                method: 'POST',
                data: { ...element, spaces: element.spaces?.map((space) => space.id) },
                onBefore: () => setIsLoading(false),
                onSuccess: setData
            });
        },
        [api]
    );

    const applyExemptions = useCallback(() => {
        if (getValues && setValue) {
            const exemptions = getValues('exemptions') || [];
            setInitialExemptions(exemptions);
            setValue('exemptions', [...exemptions, ...data.exemptions]);
        }
    }, [getValues, setValue, data]);

    const resetExemptions = useCallback(() => {
        setValue('exemptions', initialExemptions);
    }, [initialExemptions, setValue]);

    const deleteExemption = useCallback(
        (index: any) => {
            const exemptions = getValues('exemptions') || [];
            exemptions.splice(index, 1);
            setValue('exemptions', exemptions);
        },
        [getValues, setValue]
    );

    const handleAddExemption = useCallback(() => {
        if (newExemption.end !== '' && newExemption.start !== '') {
            const exemptions = getValues('exemptions') || [];
            exemptions.push(newExemption);
            setValue('exemptions', exemptions);
            setNewExemption({ start: '', end: '' });
        }
    }, [getValues, setValue, newExemption, setNewExemption]);

    useEffect(() => {
        if (element) {
            checkOverlaps(element);
        }
    }, [element]);

    return (
        <>
            <div className="tw-flex tw-flex-col tw-gap-2">
                <Loader isLoading={isLoading}>
                    {allOverlaps.length > 0 ? (
                        allOverlaps.map((elem) => (
                            <div
                                key={`${elem.title}_${elem.id}`}
                                className="tw-rounded-md tw-border tw-border-solid tw-border-red-400 tw-bg-red-200 tw-p-2"
                            >
                                <div className="tw-flex tw-justify-between">
                                    <span className="tw-text-gray-500"># {elem.id}</span>
                                    <span>{elem.title}</span>
                                </div>
                                <div className="">
                                    {moment(elem.start).format('DD.MM.YYYY')} |{' '}
                                    {`${convertTimeToString(elem.start_time)} - ${convertTimeToString(elem.end_time)}`}
                                </div>
                                {elem.description && <div className="tw-text-gray-600">{elem.description}</div>}
                            </div>
                        ))
                    ) : (
                        <span className="tw-text-gray-600">Es wurden keine Überlappungen gefunden!</span>
                    )}
                </Loader>
                {data.exemptions.length > 0 && (
                    <Button size="xs" auto onClick={applyExemptions}>
                        Als Ausnahmen eintragen
                    </Button>
                )}
                <Divider y={1} />
                <div className="tw-grid tw-grid-cols-3 tw-items-baseline tw-gap-6">
                    <DatePicker
                        placeholder="Startdatum"
                        value={newExemption.start}
                        onChange={(date) => setNewExemption({ ...newExemption, start: date })}
                    />
                    <DatePicker
                        placeholder="Startdatum"
                        value={newExemption.end}
                        onChange={(date) => setNewExemption({ ...newExemption, end: date })}
                    />
                    <Button auto type="button" onClick={handleAddExemption}>
                        <FontAwesomeIcon icon={faPlus} />
                    </Button>
                </div>
                {appliedExemptions.length > 0 && (
                    <>
                        <Divider y={1} />
                        <span>Angewendete Ausnahmen</span>
                        {appliedExemptions.map((exemption, index) => (
                            <div
                                key={index}
                                className="tw-flex tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-solid tw-border-orange-300 tw-bg-orange-200 tw-p-2"
                            >
                                <span>
                                    {moment(exemption.start).format('DD.MM.YYYY')} -{' '}
                                    {moment(exemption.end).format('DD.MM.YYYY')}
                                </span>
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    onClick={() => deleteExemption(index)}
                                    className="tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-700"
                                />
                            </div>
                        ))}
                        <Button size="xs" color="warning" auto onClick={resetExemptions}>
                            Ausnahmen zurücksetzen
                        </Button>
                    </>
                )}
            </div>
        </>
    );
};
